import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, HelpCircle, Loader2, RefreshCw } from "lucide-react";

const OTHER = "__other__";

// Questions the AI asked about a field suggestion, one at a time ("Question 2
// of 3") with Back and Next, like a chat assistant's clarifying prompt.
//
// Every question has a default that is already applied to the suggestions on
// screen, so nothing has to be answered. Picking another option (or typing
// your own under "Other") counts as a change; "Apply answers" on the last
// step re-runs the suggestion with every changed answer, without reading the
// documents again. It stays disabled until at least one answer differs from
// the suggested one.
export default function SuggestionQuestions({ questions, onApply, applying = false }) {
  const defaults = useMemo(
    () => Object.fromEntries((questions || []).map((q) => [q.id, q.default_option_id])),
    [questions],
  );
  const [choice, setChoice] = useState(defaults);
  const [otherText, setOtherText] = useState({});
  const [step, setStep] = useState(0);

  useEffect(() => {
    setChoice(defaults);
    setOtherText({});
    setStep(0);
  }, [defaults]);

  if (!questions?.length) return null;

  const total = questions.length;
  const current = questions[Math.min(step, total - 1)];
  const isLast = step >= total - 1;

  const answerFor = (q) => {
    if (choice[q.id] === OTHER) {
      const text = (otherText[q.id] || "").trim();
      return text ? { question_id: q.id, other_text: text } : null;
    }
    if (choice[q.id] && choice[q.id] !== q.default_option_id) {
      return { question_id: q.id, option_id: choice[q.id] };
    }
    return null;
  };
  const answers = questions.map(answerFor).filter(Boolean);
  const changed = (q) => answerFor(q) !== null;
  const typingNothing = choice[current.id] === OTHER && !(otherText[current.id] || "").trim();

  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10 overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-amber-200/70 dark:border-amber-800/40">
        <HelpCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {total > 1 ? `Question ${step + 1} of ${total}` : "One question"}
        </p>
        {total > 1 && (
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {questions.map((q, i) => (
              <button
                key={q.id}
                type="button"
                tabIndex={-1}
                onClick={() => setStep(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === step
                    ? "w-5 bg-purple-600"
                    : changed(q)
                      ? "w-1.5 bg-purple-400"
                      : "w-1.5 bg-amber-300 dark:bg-amber-700"
                }`}
              />
            ))}
          </div>
        )}
        <p className="ml-auto text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
          The suggestions already use the answer marked Suggested.
        </p>
      </div>

      <div className="px-4 py-3.5">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{current.question}</p>
        {current.why && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{current.why}</p>}

        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2" role="radiogroup" aria-label={current.question}>
          {[...current.options, { id: OTHER, label: "Other" }].map((o) => {
            const active = choice[current.id] === o.id;
            const isDefault = o.id === current.default_option_id;
            return (
              <div key={o.id} className={active && o.id === OTHER ? "md:col-span-2" : ""}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setChoice((prev) => ({ ...prev, [current.id]: o.id }))}
                  className={`w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                    active
                      ? "border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                      : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-600"
                  }`}
                >
                  <span
                    className={`flex-shrink-0 h-4 w-4 rounded-full border flex items-center justify-center ${
                      active ? "border-purple-600 bg-purple-600" : "border-gray-300 dark:border-gray-600"
                    }`}
                  >
                    {active && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                  </span>
                  <span className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span
                      className={`font-medium ${
                        active ? "text-purple-900 dark:text-purple-100" : "text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {o.label}
                    </span>
                    {o.evidence && (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[11px] text-gray-400 dark:text-gray-500">e.g.</span>
                        <code className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-0.5 text-xs font-mono text-gray-900 dark:text-gray-100 break-all">
                          {o.evidence}
                        </code>
                      </span>
                    )}
                  </span>
                  {isDefault && o.id !== OTHER && (
                    <span className="flex-shrink-0 rounded-full bg-purple-100 dark:bg-purple-900/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-300">
                      Suggested
                    </span>
                  )}
                </button>
                {active && o.id === OTHER && (
                  <input
                    type="text"
                    maxLength={500}
                    autoFocus
                    value={otherText[current.id] || ""}
                    onChange={(e) => setOtherText((prev) => ({ ...prev, [current.id]: e.target.value }))}
                    placeholder="Type your answer"
                    className="mt-2 w-full px-2.5 py-1.5 rounded-md border border-purple-300 dark:border-purple-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white/40 dark:bg-gray-900/20 border-t border-amber-200/60 dark:border-amber-800/30">
        <div>
          {total > 1 && (
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0 || applying}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {answers.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {answers.length} answer{answers.length !== 1 ? "s" : ""} changed
            </span>
          )}
          {!isLast ? (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
              disabled={typingNothing}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 text-xs font-medium hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-40"
            >
              Next <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              disabled={!answers.length || applying || typingNothing}
              onClick={() => onApply(answers)}
              title={!answers.length ? "Pick a different answer to update the suggestions" : undefined}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-medium disabled:opacity-40 hover:bg-purple-700 transition-colors"
            >
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {applying ? "Updating suggestions…" : "Apply answers"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
